import React, { FC, useState } from "react";
import { iaService } from '_services/ia.service'
import { voyageService } from '_services/voyage.service'
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

export interface VoyageIASearchFormProps {
  onChangeLoading: () => void;
}

interface City {

  name: string;

  nights: number;

  latitude: number;

  longitude: number;

   countryCode: string;

}

interface Destination {

  type: "country" | "city";

  name: string;

  countryCode: string;

  countryName: string;

  latitude?: number;

  longitude?: number;

}

interface Trip {

  destinations?: Destination[];

  duration?: number;

  travelers?: number;

  startDate?: string;

  endDate?: string;

  cities?: City[];

}
interface AIResponse {
  success?: boolean;
  trip?: Trip;
  error?: string;
  responseType?: string;
  missingFields?: string[];
  question?: string;

}

type AssistantRobotVisualProps = {
  size?: "sm" | "md" | "lg";
  className?: string;
};

const AssistantRobotVisual: React.FC<AssistantRobotVisualProps> = ({
  size = "md",
  className = "",
}) => {
  const sizes = {
    sm: {
      wrapper: "h-10 w-10",
      head: "h-7 w-8",
      eye: "h-1.5 w-1.5",
      antenna: "h-2.5",
      ear: "h-3 w-1.5",
      glow: "h-12 w-12",
    },
    md: {
      wrapper: "h-14 w-14",
      head: "h-9 w-11",
      eye: "h-2 w-2",
      antenna: "h-3",
      ear: "h-4 w-2",
      glow: "h-16 w-16",
    },
    lg: {
      wrapper: "h-24 w-24",
      head: "h-14 w-[72px]",
      eye: "h-3 w-3",
      antenna: "h-5",
      ear: "h-6 w-3",
      glow: "h-28 w-28",
    },
  };

  const s = sizes[size];

  return (
    <div
      className={`
        relative
        flex
        ${s.wrapper}
        items-center
        justify-center
        ${className}
      `}
    >
      <span
        className={`
          absolute
          ${s.glow}
          rounded-full
          bg-violet-500/20
          blur-xl
          dark:bg-[#FF4A52]/18
        `}
      />

      <span className="absolute -top-1 right-1 h-1.5 w-1.5 rounded-full bg-violet-400 shadow-[0_0_12px_rgba(139,92,246,0.75)] dark:bg-[#FF4A52]" />
      <span className="absolute left-1 top-2 text-[10px] text-[#FF4A52]">✦</span>
      <span className="absolute right-0 top-4 text-[9px] text-violet-400">✧</span>

      <div className="relative flex flex-col items-center">
        <span
          className={`
            ${s.antenna}
            w-0.5
            rounded-full
            bg-gradient-to-b
            from-violet-500
            to-violet-300
            dark:from-[#FF4A52]
            dark:to-[#FF4A52]/45
          `}
        />

        <div className="relative flex items-center justify-center">
          <span
            className={`
              absolute
              -left-1.5
              ${s.ear}
              rounded-full
              bg-white
              shadow-[inset_0_0_0_1px_rgba(124,58,237,0.14)]
              dark:bg-slate-900
              dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]
            `}
          />

          <div
            className={`
              relative
              ${s.head}
              rounded-[999px]
              bg-white
              shadow-[0_12px_30px_rgba(124,58,237,0.25),inset_0_0_0_1px_rgba(124,58,237,0.12)]
              dark:bg-slate-950
              dark:shadow-[0_12px_30px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.08)]
            `}
          >
            <div
              className="
                absolute
                inset-x-1.5
                top-1/2
                flex
                -translate-y-1/2
                items-center
                justify-center
                gap-1.5
                rounded-full
                bg-gradient-to-r
                from-violet-700
                via-purple-600
                to-violet-500
                py-1.5
                shadow-[0_0_18px_rgba(124,58,237,0.45)]
                dark:from-[#4c1d95]
                dark:via-[#7c3aed]
                dark:to-[#FF4A52]
              "
            >
              <span className={`${s.eye} rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]`} />
              <span className={`${s.eye} rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)]`} />
            </div>
          </div>

          <span
            className={`
              absolute
              -right-1.5
              ${s.ear}
              rounded-full
              bg-white
              shadow-[inset_0_0_0_1px_rgba(124,58,237,0.14)]
              dark:bg-slate-900
              dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]
            `}
          />
        </div>
      </div>
    </div>
  );
};

const VoyageIASearchForm: FC<VoyageIASearchFormProps> = ({
  onChangeLoading,
}) => {

  const navigate = useNavigate(); 

  const { t } = useTranslation();

  const [message, setMessage] = useState("");

  const [currentTrip, setCurrentTrip] = useState<Trip | null>(null);

  const [previousMessage, setPreviousMessage] = useState("");

  const [loading, setLoading] =
    useState(false);

  const [loadingStep, setLoadingStep] =
    useState("");

  const [aiResponse, setAiResponse] =
    useState<AIResponse | null>(null);

    const [placeholder, setPlaceholder] =
 useState(
  `${t("exemple1")}\n${t("exemple2")}\n${t("exemple3")}\n${t("exemple4")}`
);

const parseDateFR = (dateStr?: string) => {
  if (!dateStr) return null;

  const match =
    String(dateStr).match(
      /^(\d{2})\/(\d{2})\/(\d{4})$/
    );

  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  const date =
    new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const calculateDuration = (
  startDate: Date,
  endDate: Date
) => {
  const diff =
    Math.round(
      (endDate.getTime() - startDate.getTime()) /
        (1000 * 60 * 60 * 24)
    );

  return diff + 1;
};

const hasValidDestinations = (
  destinations?: Destination[]
) => {
  if (
    !destinations ||
    destinations.length === 0
  ) {
    return false;
  }

  return destinations.every((destination) => {
    const hasName =
      destination.name &&
      destination.name.trim() !== "";

    const hasCountryCode =
      destination.countryCode &&
      destination.countryCode.trim() !== "";

    /*
      Pour un pays :
      name = nom du pays
      countryCode = code pays
    */
    if (destination.type === "country") {
      return hasName && hasCountryCode;
    }

    /*
      Pour une ville :
      name = nom de ville
      countryName = nom du pays
      countryCode = code pays
    */
    if (destination.type === "city") {
      const hasCountryName =
        destination.countryName &&
        destination.countryName.trim() !== "";

      return (
        hasName &&
        hasCountryName &&
        hasCountryCode
      );
    }

    return false;
  });
};

const validateTripBeforeCreate = () => {
  const trip = currentTrip;

  if (!trip) {
    alert(t("erreurSurvenue"));
    return false;
  }

 if (
  !trip.destinations ||
  trip.destinations.length === 0
) {
  alert(t("alertDestination"));
  return false;
}

if (!hasValidDestinations(trip.destinations)) {
  alert(t("alertIADestinationInvalide"));
  return false;
}

  if (!trip.startDate) {
    alert(t("alertDateDeb"));
    return false;
  }

  if (!trip.endDate) {
    alert(t("alertDateFin"));
    return false;
  }

  const startDate =
    parseDateFR(trip.startDate);

  const endDate =
    parseDateFR(trip.endDate);

  if (!startDate) {
    alert(t("alertDateDeb"));
    return false;
  }

  if (!endDate) {
    alert(t("alertDateFin"));
    return false;
  }

  if (endDate < startDate) {
    alert(t("alertDateFin"));
    return false;
  }

  const duration =
    calculateDuration(
      startDate,
      endDate
    );

  if (duration < 2) {
    alert(t("alertNbJoursMin"));
    return false;
  }

  if (duration > 365) {
    alert(t("alertNbJoursMax"));
    return false;
  }

  if (
    trip.travelers !== undefined &&
    Number(trip.travelers) < 1
  ) {
    alert(t("alertNbVoyageurs"));
    return false;
  }

  return true;
};



const creerVoyage = async () => {


  if (!validateTripBeforeCreate()) {
    return;
  }

  voyageService.creerVoyageIA({
          currentTrip: currentTrip
        })
          .then((response: any) => {
     /* if(onChangeLoading)
        {
          onChangeLoading();
        }*/
        console.log("response", response);
  
           const idVoyage = response.data.idVoyage;
            if (idVoyage && idVoyage !== 0) {
                
                // Redirect to the specific planificateur-voyage page with idVoyage
               navigate(`${t('lienPlanificateur')}/${idVoyage}`);
  
            } else {
              // Gérer le cas où idVoyage est 0 ou null
            }
          })
          .catch((error: any) => {

            console.error(error);
          });
}

const getTotalNights = () => {

  return (
    currentTrip?.cities?.reduce(
      (total, city) =>
        total + city.nights,
      0
    ) || 0
  );

};

const getMaxNights = () => {

  return (
    (currentTrip?.duration || 1) - 1
  );

};

const removeCity = (
  indexToRemove: number
) => {

  if (!currentTrip?.cities) {
    return;
  }

  const updatedCities =
    currentTrip.cities.filter(
      (_, index) =>
        index !== indexToRemove
    );

  setCurrentTrip({
    ...currentTrip,
    cities: updatedCities
  });

  setAiResponse((prev: any) => ({
    ...prev,
    trip: {
      ...prev.trip,
      cities: updatedCities
    }
  }));

};

const removeAllCities = () => {

  if (!currentTrip) {
    return;
  }

  setCurrentTrip({
    ...currentTrip,
    cities: []
  });

  setAiResponse((prev: any) => ({
    ...prev,
    trip: {
      ...prev.trip,
      cities: []
    }
  }));

};

const changeCityNights = (
  cityIndex: number,
  delta: number
) => {

  if (!currentTrip?.cities) {
    return;
  }

  const updatedCities = [
    ...currentTrip.cities
  ];

  const city =
    updatedCities[cityIndex];

  const totalNights =
    getTotalNights();

  const maxNights =
    getMaxNights();

  // AJOUT D'UNE NUIT
  if (delta > 0) {

    if (
      totalNights >= maxNights
    ) {

      alert(
        `Le total des nuits ne peut pas dépasser ${maxNights}`
      );

      return;

    }

    city.nights += 1;

  }

  // RETRAIT D'UNE NUIT
  if (delta < 0) {

    if (city.nights <= 1) {
      return;
    }

    city.nights -= 1;

  }

  setCurrentTrip({
    ...currentTrip,
    cities: updatedCities
  });

  setAiResponse((prev: any) => ({
    ...prev,
    trip: {
      ...prev.trip,
      cities: updatedCities
    }
  }));

  console.log("updatedCities",updatedCities);

};

const moveCityUp = (
  cityIndex: number
) => {

  if (
    !currentTrip?.cities ||
    cityIndex === 0
  ) {
    return;
  }

  const updatedCities = [
    ...currentTrip.cities
  ];

  [
    updatedCities[cityIndex - 1],
    updatedCities[cityIndex]
  ] = [
    updatedCities[cityIndex],
    updatedCities[cityIndex - 1]
  ];

  setCurrentTrip({
    ...currentTrip,
    cities: updatedCities
  });

  setAiResponse((prev: any) => ({
    ...prev,
    trip: {
      ...prev.trip,
      cities: updatedCities
    }
  }));

};

const moveCityDown = (
  cityIndex: number
) => {

  if (
    !currentTrip?.cities ||
    cityIndex ===
      currentTrip.cities.length - 1
  ) {
    return;
  }

  const updatedCities = [
    ...currentTrip.cities
  ];

  [
    updatedCities[cityIndex],
    updatedCities[cityIndex + 1]
  ] = [
    updatedCities[cityIndex + 1],
    updatedCities[cityIndex]
  ];

  setCurrentTrip({
    ...currentTrip,
    cities: updatedCities
  });

  setAiResponse((prev: any) => ({
    ...prev,
    trip: {
      ...prev.trip,
      cities: updatedCities
    }
  }));

};

const handleGenerateTrip = async () => {

  if (!message.trim()) return;

  try {

    //onChangeLoading();

    setLoading(true);

    setAiResponse(null);

    setLoadingStep(
       t("analyseDemande")
    );

    setTimeout(() => {

      setLoadingStep(
        t("generationVoyage")
      );

    }, 2000);

    setTimeout(() => {

      setLoadingStep(
        t("resultatApproche")
      );

    }, 2000);

    console.log("currentTrip",currentTrip);
     console.log("previousMessage",previousMessage);

    iaService.creerVoyage({
     message,
     currentTrip,
     previousMessage
    })
    .then((response: any) => {

      console.log(response);

      setAiResponse(response.data);


     if (response.data.trip) {

      setCurrentTrip(
        response.data.trip
      );

    }

    setPreviousMessage(message);

    setMessage("");

    setPlaceholder(
  t('completezDemande')
);

    })
    .catch((error: any) => {

      console.error(error);

  // RATE LIMIT
  if (
    error.response?.status === 428
  ) {

    setAiResponse({

      error:
        t('limiteIA1')

    });

        return;
  }
  else if (
    error.response?.status === 429
  ) {

    setAiResponse({

      error:
       t('limiteIA2')

    });

    return;

  }

  // MESSAGE API PERSONNALISÉ
  if (
    error.response?.data?.error
  ) {

    setAiResponse({

      error:
        error.response.data.error

    });

    return;

  }

  // ERREUR GENERIQUE
  setAiResponse({

    error:
      t('erreurSurvenue')

  });

    })
    .finally(() => {

      setLoading(false);

    });

  } catch (error: any) {

   console.error(error);

  // RATE LIMIT
  if (
    error.response?.status === 429
  ) {

    setAiResponse({

      error:
        t('limiteIA2')

    });

    return;

  }

  // MESSAGE API PERSONNALISÉ
  if (
    error.response?.data?.error
  ) {

    setAiResponse({

      error:
        error.response.data.error

    });

    return;

  }

  // ERREUR GENERIQUE
  setAiResponse({

    error:
      t('erreurSurvenue')

  });

    setLoading(false);

  }
};

const getCountryName = (
  countryCode: string
) => {

  const destinations =
    aiResponse?.trip?.destinations?.find(
      (d) =>
        d.countryCode ===
        countryCode
    );

  if (destinations) {
    return destinations.name;
  }

  return countryCode;

};

  return (

    <div className="w-full mt-2 sm:mt-3">

      <div
        className="
          bg-white
          dark:bg-neutral-800
          rounded-[40px]
          shadow-xl
          p-5 sm:p-6
        "
      >

        {/* HEADER */}

        <div className="mb-4 sm:mb-6 flex items-start justify-between gap-4">
  <div className="min-w-0">
    <div className="mb-2 flex items-center gap-2">
      <AssistantRobotVisual size="sm" className="shrink-0" />

      <h2
        className="
          text-sm
          sm:text-2xl
          font-black
          text-neutral-950
          dark:text-white
        "
      >
        {t("creerVoyageAssistant")} 
      </h2>
  <div className="relative -top-3 ml-0 inline-flex group">
  <button
    type="button"
    aria-label={t("modalPlannerAI.betaLabel")}
    className="
      inline-flex
      items-center
      rounded-full
      bg-violet-50
      px-1.5
      py-0
      text-[9px]
      font-semibold
      uppercase
      tracking-[0.1em]
      text-violet-600
      ring-1
      ring-violet-200
      cursor-help
      transition
      hover:bg-violet-100
      dark:bg-violet-900/30
      dark:text-violet-200
      dark:ring-violet-700
    "
  >
    BETA
  </button>

<div
  role="tooltip"
  className="
    pointer-events-none
    absolute
    right-0
    top-full
    z-[9999]
    mt-2
    w-[240px]
    max-w-[calc(100vw-32px)]
    rounded-xl
    bg-[#0f172a]
    px-3
    py-2
    text-[11px]
    font-medium
    leading-snug
    text-white
    opacity-0
    shadow-2xl
    ring-1
    ring-white/10
    transition-opacity
    duration-200
    group-hover:opacity-100
    group-focus-within:opacity-100

    sm:left-1/2
    sm:right-auto
    sm:w-64
    sm:-translate-x-1/2
  "
>
    {t("aiTripBetaNotice")}
  </div>
</div>
    </div>

    <p
      className="
        text-xs
        sm:text-base
        text-neutral-500
        dark:text-neutral-400
      "
    >
      {t("decrivezVoyage")}
    </p>
  </div>

  <AssistantRobotVisual
    size="lg"
    className="hidden shrink-0 sm:flex"
  />
</div>

        {/* TEXTAREA */}

        <textarea
          value={message}

          onChange={(e) =>
            setMessage(
              e.target.value
            )
          }

          placeholder={placeholder}
          data-focus-lieu="true"
          className="
            w-full
            h-40
            sm:h-44
            rounded-3xl
            border
            border-neutral-200
            dark:border-neutral-700
            bg-neutral-50
            dark:bg-neutral-900
            p-3
            sm:p-5
            resize-none
            outline-none
            text-xs
            sm:text-sm
            leading-6
            sm:leading-7
          "
        />

        {/* ACTIONS */}

        <div
          className="
            flex
            justify-end
            mt-5
          "
        >
          <button
           className="
              px-4 sm:px-7
py-2 sm:py-3
text-xs sm:text-base
              rounded-full
             boutonVoyage
              text-white
              font-medium
              transition
              disabled:opacity-50
            "
            onClick={() => {

            setCurrentTrip(null);

            setPreviousMessage("");

            setAiResponse(null);

            setMessage("");

              setPlaceholder(`${t("exemple1")}\n${t("exemple2")}\n${t("exemple3")}\n${t("exemple4")}`);

          }}
          >
            <i className={`las la-broom text-sm sm:text-lg`}></i> {t('reinitialiser')}
          </button>
          <button
            onClick={
              handleGenerateTrip
            }

            disabled={loading}

            className="
              px-4 sm:px-7
py-2 sm:py-3
text-xs sm:text-base
              rounded-full
              bg-red-500
              hover:bg-red-600
              text-white
              font-medium
              transition
              disabled:opacity-50
            "
          >

            {
              loading
                ? t('veuillezPatienter')
                : t('envoyerIA')
            }

          </button>

        </div>

        {/* LOADING */}

        {
          loading && (

            <div
              className="
                mt-6
                p-5
                rounded-3xl
                bg-neutral-50
                dark:bg-neutral-900
                border
                border-neutral-200
                dark:border-neutral-700
              "
            >

             <div
  className="
    flex
    items-center
    gap-3
  "
>
  <AssistantRobotVisual size="sm" className="animate-pulse" />

  <div>
    <p
      className="
        text-xs
        sm:text-base
        font-black
        text-neutral-950
        dark:text-white
      "
    >
      {loadingStep}
    </p>

    <p className="mt-0.5 text-[11px] sm:text-xs text-neutral-500 dark:text-neutral-400">
      {t("aiTripPreparing")}
    </p>
  </div>
</div>

            </div>

          )
        }

        {/* RESULTAT IA */}

                {
  aiResponse?.responseType ===
    "need_more_info" && (

    <div
      className="
        mt-6
        p-4 sm:p-6
        rounded-3xl
        bg-yellow-50
        dark:bg-yellow-900/20
        border
        border-yellow-200
        dark:border-yellow-800
      "
    >

      <h3
        className="
          text-lg
          font-semibold
          mb-3
        "
      >
        {t('informationsManquantes')}
      </h3>

      <p
        className="
          text-xs sm:text-base
          mb-4
        "
      >
        {
          aiResponse.question
        }
      </p>

     {/* 
        aiResponse.missingFields &&
        aiResponse.missingFields.length > 0 && (

          <div
            className="
              flex
              flex-wrap
              gap-2
            "
          >

            {
              aiResponse.missingFields.map(
                (
                  field,
                  index
                ) => (

                  <div
                    key={index}

                    className="
                      px-3
                      py-1
                      rounded-full
                      bg-yellow-100
                      dark:bg-yellow-800
                      text-sm
                    "
                  >

                    {field}

                  </div>

                )
              )
            }

          </div>

        )
    */  }

    </div>

  )
}

        {
          aiResponse?.trip && aiResponse?.responseType !== "need_more_info" && (

            <div
              className="
                mt-6
                p-4
                rounded-3xl
                bg-neutral-50
                dark:bg-neutral-900
                border
                border-neutral-200
                dark:border-neutral-700
              "
            >
              <div
  className="
    flex
    items-center
    justify-between
    mb-5
  "
>

              <h3
                className="
                  text-sm sm:text-xl
                  font-semibold
                  mb-5 dark:text-white
                "
              >
                {t("aiTripGenerated")}
              </h3>

              {
    aiResponse?.responseType ===
      "complete" && (

      <button
        className="
         px-3 sm:px-7
py-1 sm:py-3
text-xs sm:text-base
              rounded-full
              bg-red-500
              hover:bg-red-600
              text-white
              font-medium
              transition
              disabled:opacity-50
        "
        onClick={() => {

          creerVoyage();

        }}
      >
        <i className={`las la-hammer text-sm sm:text-lg`}></i> {t('creerLeVoyage')}
      </button>

    )
  }
  </div>

              {/* INFOS PRINCIPALES */}

             {/* INFOS PRINCIPALES */}

<div className="space-y-4">

  {/* LIGNE 1 : DESTINATION + VOYAGEURS */}

  <div
    className="
      grid
      grid-cols-1
      md:grid-cols-2
      gap-4
    "
  >

    {
      aiResponse.trip.destinations && aiResponse.trip.destinations.length === 1 &&(

        <div
          className="
            p-3 sm:p-4
            rounded-2xl
            bg-white
            dark:bg-neutral-800
          "
        >

          <p
            className="
              text-xs sm:text-base
              text-neutral-500
            "
          >
            {t("destination")}
          </p>

          <p
            className="
            text-xs sm:text-base
              font-semibold
              mt-1
            "
          >
            {
              aiResponse.trip
                .destinations[0].name
            }
          </p>

        </div>

      )
    }

    {
      aiResponse.trip.destinations &&
      aiResponse.trip.destinations.length > 1 && (

        <div
          className="
            p-3 sm:p-4
            rounded-2xl
            bg-white
            dark:bg-neutral-800
          "
        >

          <p
            className="
             text-xs sm:text-base
              text-neutral-500
              mb-2
            "
          >
            {t("destinations")}
          </p>

          <div
            className="
              flex
              flex-wrap
              gap-1 sm:gap-2
            "
          >

            {
              aiResponse.trip.destinations.map(
                (
                  destination,
                  index
                ) => (

                  <div
                    key={index}

                    className="
                      px-3
                      py-1
                      rounded-full
                      bg-neutral-100
                      dark:bg-neutral-700
                      text-xs sm:text-sm
                      font-medium
                    "
                  >

                    {destination.name}

                  </div>

                )
              )
            }

          </div>

        </div>

      )
    }

   {
  (
    <div
      className="
        p-3 sm:p-4
        rounded-2xl
        bg-white
        dark:bg-neutral-800
      "
    >

      <p
        className="
          text-xs sm:text-base
          text-neutral-500
        "
      >
        {t('nombreVoyageur')}{
      (aiResponse.trip?.travelers ?? 1) > 1
        ? "s"
        : ""
    }
      </p>

      <p
        className="
          text-xs sm:text-base
          font-semibold
          mt-1
        "
      >
        {
          aiResponse.trip?.travelers ?? 1
        }
      </p>

    </div>
  )
}

  </div>

  {/* LIGNE 2 : DATES + DURÉE */}

  <div
    className="
      grid
      grid-cols-1
      md:grid-cols-2
      gap-4
    "
  >

    {
      (
        aiResponse.trip.startDate ||
        aiResponse.trip.endDate
      ) && (

        <div
          className="
            p-3 sm:p-4
            rounded-2xl
            bg-white
            dark:bg-neutral-800
          "
        >

          <p
            className="
              text-xs sm:text-base
              text-neutral-500
            "
          >
            {t("dates")}
          </p>

          <p
            className="
            text-xs sm:text-base
              font-semibold
              mt-1
            "
          >
            {
              aiResponse.trip
                .startDate
            }
            {" - "}
            {
              aiResponse.trip
                .endDate
            }
          </p>

        </div>

      )
    }

    {
      aiResponse.trip
        .duration && (

        <div
          className="
            p-3 sm:p-4
            rounded-2xl
            bg-white
            dark:bg-neutral-800
          "
        >

          <p
            className="
              text-xs sm:text-base
              text-neutral-500
            "
          >
            {t('duree')}
          </p>

          <p
            className="
            text-xs sm:text-base
              font-semibold
              mt-1
            "
          >
            {
              aiResponse.trip
                .duration
            } {t("jours")}
          </p>

        </div>

      )
    }

  </div>

</div>

              {/* VILLES */}

              {
                aiResponse.trip.cities &&
                aiResponse.trip.cities
                  .length > 0 && (

                  <div className="mt-6">
                    <div className="mb-4">

                      <h4
                        className="
                          font-semibold
                          text-sm sm:text-lg
                          mb-3 dark:text-white
                        "
                      >
                        {t('suggestionItineraire')}
                      </h4>

                      <div className="flex justify-end">

                        <button
                          className="
                              px-3 sm:px-4
                              py-1 sm:py-2
                              text-xs sm:text-sm
                            rounded-full
                            bg-primary-700
                            text-white
                            
                          "
                          onClick={removeAllCities}
                        >
                          {t('effacerSuggestion')}
                        </button>

                      </div>

                    </div>

                    <div
                      className="
                        space-y-3
                      "
                    >

                      {
currentTrip?.cities?.map(
  (
    city,
    index
  ) => {

    const previousCity =
      currentTrip.cities?.[
        index - 1
      ];

    const showCountryTitle =
      !previousCity ||
      previousCity.countryCode !==
        city.countryCode;

    return (

      <div key={index}>

        {
          showCountryTitle && (

            <h5
              className="
                text-xs sm:text-sm
                font-semibold
                uppercase
                tracking-wide
                text-neutral-500
                mb-3
                mt-6
                px-1
              "
            >
              {
                getCountryName(
                  city.countryCode
                )
              }
            </h5>

          )
        }

        <div
          className="
            flex
            items-center
            justify-between
            p-2 sm:p-4
            rounded-2xl
            bg-white
            dark:bg-neutral-800
            mb-3
          "
        >

          <div>

            <p
              className="
                text-xs sm:text-base dark:text-white
              "
            >
              {city.name}
            </p>

          </div>

          <div
            className="
              flex
              items-center
              gap-1 sm:gap-2
              flex-wrap
            "
          >

            <button
              className="
                w-5 h-5 sm:w-8 sm:h-8
                rounded-full
                bg-neutral-200
                dark:bg-neutral-700
              "
              onClick={() =>
                moveCityUp(index)
              }
            >
              ↑
            </button>

            <button
              className="
                w-5 h-5 sm:w-8 sm:h-8
                rounded-full
                bg-neutral-200
                dark:bg-neutral-700
              "
              onClick={() =>
                moveCityDown(index)
              }
            >
              ↓
            </button>

            <button
              className="
                w-5 h-5 sm:w-8 sm:h-8
                rounded-full
                bg-neutral-200
                dark:bg-neutral-700
              "
              onClick={() =>
                changeCityNights(
                  index,
                  -1
                )
              }
            >
              -
            </button>

            <div
              className="
                text-xs sm:text-sm
                text-neutral-500
                sm:min-w-[70px]
                text-center
              "
            >
              {city.nights} {t('nuitMinuscule')}{
      (city.nights ?? 1) > 1
        ? "s"
        : ""
    }
            </div>

            <button
              className="
                w-5 h-5 sm:w-8 sm:h-8
                rounded-full
                bg-neutral-200
                dark:bg-neutral-700
              "
              onClick={() =>
                changeCityNights(
                  index,
                  1
                )
              }
            >
              +
            </button>

            <button
              className="
                w-5 h-5 sm:w-8 sm:h-8
                rounded-full
               bg-primary-700
                text-white
                text-sm
              "
              onClick={() =>
                removeCity(index)
              }
            >
              <i className={`las la-trash text-lg`}></i> 
            </button>

          </div>

        </div>

      </div>

    );

  }
)
}

                    </div>

                  </div>

                )
              }

            </div>

          )
        }



        {/* ERREUR */}

        {
          aiResponse?.error && (

            <div
              className="
                mt-6
                p-2 sm:p-4
                rounded-2xl
                bg-red-50
                text-red-500
              "
            >

              {aiResponse.error}

            </div>

          )
        }

      </div>

    </div>

  );
};

export default VoyageIASearchForm;
